import React, { useEffect, useRef, useState } from 'react'

import { AgGridCellValueMethod } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { ICashflowReport } from '@/interfaces/portfolioOptimization.interface'
import {
  getCashflowReport,
  getUpdatedCellValue,
} from '@/services/portfolio-optimization/optimization-reports.service'
import { showSnackbar } from '@/states/snackbar.slice'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import {
  GridOptions,
  GridReadyEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Box } from '@mui/material'
import { useDispatch } from 'react-redux'
import { CashflowReportColumns } from './CashflowReportColumns'

const CashFlowReportGrid: React.FC<{
  setGridReference: Function
  onDataChange: (hasData: boolean) => void 
}> = ({ setGridReference, onDataChange }) => {
  const appDispatch = useDispatch()
  const selectedReport =
    useAppSelector((s) => s.axiomReport.selectedReport) || {}
  const [cashflowReportData, setCashflowReportData] = useState<
    ICashflowReport[]
  >([])
  const [loading, setLoading] = useState<boolean>(true)
  const gridRef = useRef<AgGridReact>(null)
  const gridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    rowSelection: 'single' as const,
    columnDefs: CashflowReportColumns.map((item) => {
      if (item.cellDataType === 'number') {
        ;(item.valueGetter = (params: ValueGetterParams) =>
          getUpdatedCellValue(
            params.data,
            params.colDef.field as string,
            AgGridCellValueMethod.VALUE_GETTER,
          )),
          (item.valueFormatter = (params: ValueFormatterParams) =>
            getUpdatedCellValue(
              params.data,
              params.colDef.field as string,
              AgGridCellValueMethod.VALUE_FORMATTER,
            ))
      }
      return item
    }),
  } as GridOptions

  const onGridReady = (params: GridReadyEvent) => {
    setGridReference(gridRef)
  }

  useEffect(() => {
    setLoading(true)
    const fetchCashflowReport = async () => {
      try {
        const response = await getCashflowReport(selectedReport.reportId)
        setCashflowReportData(response)
        onDataChange(response.length > 0)
      } catch (err) {
        appDispatch(
          showSnackbar({
            message: `Error fetching cashflow reports ${err instanceof Error ? err.message : String(err)}`,
            severity: 'error',
          }),
        )
        setCashflowReportData([])
        onDataChange(false)
      } finally {
        setLoading(false)
      }
    }
    fetchCashflowReport()
  }, [selectedReport.reportId, appDispatch, onDataChange])

  return (
    <Box
      style={{ paddingBottom: '2rem', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box 
        className='ag-theme-quartz cashflow-report-table'
        style={{ height: cashflowReportData.length === 0 ? '200px' : 'flex-grow: 1', minHeight: '200px' }}
      >
        <AgGridReact
          onGridReady={onGridReady}
          ref={gridRef}
          rowData={cashflowReportData}
          gridOptions={gridOptions}
          loading={loading}
          data-cy='cashflow-report-ag-grid'
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default CashFlowReportGrid
 